import { IconFlag } from '@tabler/icons-react-native'
import { clsx } from 'clsx'
import { ComponentRef, useCallback, useRef, useState, type FC } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type ProfileReportButton_profile$key } from '@/__generated__/ProfileReportButton_profile.graphql'
import { Dialog, ProfileButton, Text, TextAreaInput, VStack, type ProfileButtonProps } from '@/components'
import { useDisclosure, useReportMutation } from '@/hooks'

export interface ProfileReportButtonProps extends Omit<ProfileButtonProps, 'label' | 'onPress'> {
  label?: string
  onConfirm?: () => void
  profileRef: ProfileReportButton_profile$key | null | undefined
}

export const ProfileReportButton: FC<ProfileReportButtonProps> = ({
  className,
  isDisabled: _isDisabled,
  label = 'Report',
  onConfirm,
  profileRef,
  ...props
}) => {
  const buttonRef = useRef<ComponentRef<typeof ProfileButton>>(null)
  const profileData = useFragment(
    graphql`
      fragment ProfileReportButton_profile on Profile {
        name
        userId
      }
    `,
    profileRef
  )
  const [message, setMessage] = useState('')
  const [report, isReporting] = useReportMutation()
  const [showReportDialog, reportDialog] = useDisclosure(false)
  const isDisabled = _isDisabled || isReporting

  return (
    <>
      <ProfileButton
        accessibilityLabel={`Report ${profileData?.name ?? 'this profile'}`}
        className={clsx(
          // 'bg-orange-500 data-[hover=true]:bg-orange-600 data-[active=true]:bg-orange-600',
          className
        )}
        Icon={IconFlag}
        isDisabled={isDisabled}
        label={label}
        onPress={reportDialog.open}
        ref={buttonRef}
        {...props}
      />
      <Dialog
        finalFocusRef={buttonRef}
        isDisabled={isDisabled}
        isOpen={showReportDialog}
        onClose={reportDialog.close}
        onConfirm={useCallback(async () => {
          await report(profileData?.userId, message)
          reportDialog.close()
          onConfirm?.()
        }, [message, onConfirm, profileData?.userId, report, reportDialog])}
        title={`Report ${profileData?.name ?? 'Profile'}`}
      >
        <VStack className='gap-4'>
          <Text>Are you sure you want to report {profileData?.name ?? 'this profile'}?</Text>
          <TextAreaInput
            textareaInputProps={{
              placeholder: `Please tell us why you are reporting ${profileData?.name ?? 'this profile'}. Be as detailed as you need.`
            }}
            isDisabled={isDisabled}
            isRequired
            label='Reason'
            onChange={setMessage}
            value={message}
          />
        </VStack>
      </Dialog>
    </>
  )
}
